import React, { useState } from 'react';
import {
  Dialog,
  DialogContent,
  Box,
  Typography,
  Button,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  useTheme,
  useMediaQuery
} from '@mui/material';
import {
  WhatsApp as WhatsAppIcon,
  AccountCircle as AccountIcon,
  Analytics as AnalyticsIcon,
  CheckCircle as CheckIcon,
  Phone as PhoneIcon,
  Message as MessageIcon,
  Timeline as TimelineIcon
} from '@mui/icons-material';

const OnboardingWizard = ({ open, onClose, onComplete, userName }) => {
  const [activeStep, setActiveStep] = useState(0);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const steps = [
    {
      label: 'Welcome to WhatsApp API Portal',
      icon: <WhatsAppIcon />,
      content: {
        title: `Welcome, ${userName || 'User'}!`,
        description: 'Get started with the WhatsApp Business API platform to send messages, manage templates, and track analytics.',
        features: [
          { icon: <PhoneIcon />, text: 'Connect multiple WhatsApp Business accounts' },
          { icon: <MessageIcon />, text: 'Send messages and manage conversations' },
          { icon: <TimelineIcon />, text: 'Track analytics and conversation insights' }
        ]
      }
    },
    {
      label: 'Connect Your First Account',
      icon: <AccountIcon />,
      content: {
        title: 'Ready to Connect?',
        description: 'Connect your WhatsApp Business Account to start sending messages and accessing powerful features.',
        features: [
          { icon: <CheckIcon />, text: 'Secure connection with Meta Business' },
          { icon: <CheckIcon />, text: 'Real-time message delivery' },
          { icon: <CheckIcon />, text: 'Template message support' }
        ]
      }
    },
    {
      label: 'Explore Analytics',
      icon: <AnalyticsIcon />,
      content: {
        title: 'Track Your Success',
        description: 'Once connected, you\'ll see detailed analytics about your message performance and conversation insights.',
        features: [
          { icon: <CheckIcon />, text: 'Message delivery rates' },
          { icon: <CheckIcon />, text: 'Conversation categories' },
          { icon: <CheckIcon />, text: 'Cost tracking and billing' }
        ]
      }
    }
  ];

  const handleNext = () => {
    if (activeStep === steps.length - 1) {
      onComplete();
    } else {
      setActiveStep((prevActiveStep) => prevActiveStep + 1);
    }
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      fullScreen={isMobile}
      PaperProps={{
        sx: {
          borderRadius: isMobile ? 0 : 3,
          minHeight: isMobile ? '100vh' : '600px'
        }
      }}
    >
      <DialogContent sx={{ p: 0 }}>
        <Box sx={{ 
          background: 'linear-gradient(135deg, #25D366 0%, #128C7E 100%)',
          color: 'white',
          p: { xs: 3, sm: 4 },
          textAlign: 'center'
        }}>
          <WhatsAppIcon sx={{ fontSize: 48, mb: 2 }} />
          <Typography variant="h4" gutterBottom sx={{ fontWeight: 700 }}>
            WhatsApp API Portal
          </Typography>
          <Typography variant="subtitle1" sx={{ opacity: 0.9 }}>
            Your gateway to WhatsApp Business messaging
          </Typography>
        </Box>

        <Box sx={{ p: { xs: 2, sm: 4 } }}>
          {isMobile ? (
            // Mobile: Single step view
            <Box>
              <Box sx={{ mb: 3, textAlign: 'center' }}>
                <Typography variant="overline" color="text.secondary">
                  Step {activeStep + 1} of {steps.length}
                </Typography>
              </Box>
              
              <Card elevation={0} sx={{ mb: 4, bgcolor: 'rgba(37, 211, 102, 0.05)' }}>
                <CardContent sx={{ textAlign: 'center', py: 4 }}>
                  <Box sx={{ color: '#25D366', mb: 2 }}>
                    {React.cloneElement(steps[activeStep].icon, { sx: { fontSize: 48 } })}
                  </Box>
                  <Typography variant="h5" gutterBottom sx={{ fontWeight: 600 }}>
                    {steps[activeStep].content.title}
                  </Typography>
                  <Typography variant="body1" sx={{ mb: 3, color: 'text.secondary' }}>
                    {steps[activeStep].content.description}
                  </Typography>
                  
                  <List sx={{ maxWidth: 400, mx: 'auto' }}>
                    {steps[activeStep].content.features.map((feature, index) => (
                      <ListItem key={index} sx={{ px: 0 }}>
                        <ListItemIcon sx={{ minWidth: 36, color: '#25D366' }}>
                          {feature.icon}
                        </ListItemIcon>
                        <ListItemText 
                          primary={feature.text}
                          primaryTypographyProps={{ fontSize: '0.9rem' }}
                        />
                      </ListItem>
                    ))}
                  </List>
                </CardContent>
              </Card>
            </Box>
          ) : (
            // Desktop: Stepper view
            <Stepper activeStep={activeStep} orientation="vertical">
              {steps.map((step, index) => (
                <Step key={step.label}>
                  <StepLabel
                    icon={
                      <Box sx={{ 
                        bgcolor: activeStep >= index ? '#25D366' : 'grey.300',
                        color: 'white',
                        borderRadius: '50%',
                        width: 40,
                        height: 40,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}>
                        {React.cloneElement(step.icon, { sx: { fontSize: 20 } })}
                      </Box>
                    }
                  >
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                      {step.label}
                    </Typography>
                  </StepLabel>
                  <StepContent>
                    <Card elevation={0} sx={{ mt: 2, mb: 3, bgcolor: 'rgba(37, 211, 102, 0.05)' }}>
                      <CardContent>
                        <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                          {step.content.title}
                        </Typography>
                        <Typography variant="body1" sx={{ mb: 2, color: 'text.secondary' }}>
                          {step.content.description}
                        </Typography>
                        
                        <List dense>
                          {step.content.features.map((feature, featureIndex) => (
                            <ListItem key={featureIndex} sx={{ px: 0 }}>
                              <ListItemIcon sx={{ minWidth: 36, color: '#25D366' }}>
                                {feature.icon}
                              </ListItemIcon>
                              <ListItemText 
                                primary={feature.text}
                                primaryTypographyProps={{ fontSize: '0.9rem' }}
                              />
                            </ListItem>
                          ))}
                        </List>
                      </CardContent>
                    </Card>
                  </StepContent>
                </Step>
              ))}
            </Stepper>
          )}

          <Box sx={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center',
            mt: 4,
            pt: 3,
            borderTop: '1px solid',
            borderColor: 'divider'
          }}>
            <Button
              disabled={activeStep === 0}
              onClick={handleBack}
              sx={{ mr: 1 }}
            >
              Back
            </Button>
            
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {steps.map((_, index) => (
                <Box
                  key={index}
                  sx={{
                    width: 8,
                    height: 8,
                    borderRadius: '50%',
                    bgcolor: activeStep >= index ? '#25D366' : 'grey.300',
                    transition: 'background-color 0.3s ease'
                  }}
                />
              ))}
            </Box>
            
            <Button
              variant="contained"
              onClick={handleNext}
              sx={{ 
                bgcolor: '#25D366',
                '&:hover': { bgcolor: '#128C7E' },
                minWidth: 120
              }}
            >
              {activeStep === steps.length - 1 ? 'Get Started' : 'Next'}
            </Button>
          </Box>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default OnboardingWizard;